import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Button } from './Button.js';
import { Icon, type IconName } from './Icon.js';

/**
 * Loading, empty and error states (§30, §31, §32).
 *
 * Nothing in this app ever renders a blank region: a surface is either showing
 * data, a skeleton shaped like that data, an empty state with a way forward, or
 * an error with a working retry.
 */

export function Skeleton({
  width,
  height = 16,
  radius = 'var(--radius-sm)',
  className,
}: {
  width?: number | string;
  height?: number | string;
  radius?: string;
  className?: string;
}) {
  return (
    <span
      className={clsx('skeleton', className)}
      style={{ width: width ?? '100%', height, borderRadius: radius }}
      aria-hidden
    />
  );
}

export function SkeletonRows({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={clsx('skeleton-rows', className)} aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="skeleton-row">
          <Skeleton width={36} height={36} radius="var(--radius-md)" />
          <div className="skeleton-row__body">
            <Skeleton width={`${55 + ((index * 13) % 25)}%`} height={13} />
            <Skeleton width={`${30 + ((index * 17) % 20)}%`} height={11} />
          </div>
          <Skeleton width={54} height={13} />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon = 'sparkle',
  title,
  description,
  action,
}: {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state__glyph">
        <Icon name={icon} size={22} />
      </span>
      <p className="t-h3">{title}</p>
      {description ? <p className="t-small text-tertiary">{description}</p> : null}
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}

/**
 * Copy comes from the caller, translated there.
 *
 * This component stays free of the translator: it is presentational, it is
 * rendered inside the error boundary's fallback (which must work even when the
 * provider above it is what failed), and a default sentence baked in here is a
 * sentence no translator can reach.
 */
export function ErrorState({
  title = 'Something went wrong',
  description,
  onRetry,
  retryLabel = 'Try again',
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div className="error-state" role="alert">
      <span className="error-state__glyph">
        <Icon name="alert" size={22} />
      </span>
      <p className="t-h3">{title}</p>
      {description ? <p className="t-small text-secondary">{description}</p> : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" iconLeft="refresh" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * First paint, before any data exists.
 *
 * `progress`, when given, is how much of the artwork is in hand — 0 to 1. The
 * screens are painted pictures, so the app waits for them here rather than
 * building itself in front of the user, and the bar says how long that is
 * going to take instead of leaving a logo pulsing at nothing.
 */
export function SplashScreen({
  message = 'Opening the mine…',
  progress,
  progressLabel = 'Loading the artwork',
}: {
  message?: string;
  progress?: number;
  /** The progress bar's accessible name, translated by the caller. */
  progressLabel?: string;
}) {
  /*
   * The entry screen is the CraftGem artwork itself, with the loading bar it
   * draws: the gold fills that bar as the artwork for the first screen arrives.
   * Before the size of the download is known the bar shows empty.
   */
  const pct = progress === undefined ? 0 : Math.round(Math.min(1, Math.max(0, progress)) * 100);
  return (
    <div className="boot">
      <div className="boot__backdrop" aria-hidden />
      <div className="boot__art">
        <div
          className="boot__track"
          role="progressbar"
          aria-label={progressLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          {pct > 0 ? <i className="boot__fill" style={{ width: `${pct}%` }} /> : null}
        </div>
      </div>
      <p className="visually-hidden" role="status">
        {message}
      </p>
    </div>
  );
}
